import { Component, OnInit } from '@angular/core';
import { Router } from '@angular/router';
import { Profile } from '../../../models/Profile';
import { ProfilesService } from '../../../services/profiles.service';
import { ErrorService } from '../../../shared/services/error.service';
import { TableAction } from '../../../shared/components/table/table.component';
import { AuthService } from '../../../services/auth.service';
import { ConfirmationService } from '../../../shared/services/confirmation.service';
import { take } from 'rxjs';

@Component({
  selector: 'app-profiles-list',
  templateUrl: './profiles-list.component.html',
  styleUrls: ['./profiles-list.component.css']
})
export class ProfilesListComponent implements OnInit {
  profiles: Profile[] = [];
  loading = false;
  errorMessage: string | null = null;

  searchTerm = '';
  
  // Controles de paginação
  currentPage = 1;
  pageSize = 10;
  totalItems = 0;
  totalPages = 0;

  columns: { titulo: string, label: string, formatter?: (value: any, item?: any) => string }[] = [
    { titulo: 'Nome', label: 'name' },
    { titulo: 'Descrição', label: 'description' },
    { titulo: 'Atualizado por', label: 'updatedByName', formatter: (value: any, item?: any) => value || item?.updatedBy || '-' },
    { titulo: 'Qtd Permissões', label: 'permissionOverrides', formatter: (value: any) => value?.length || 0 },
    { titulo: 'Última Atualização', label: 'updatedAt', formatter: (value: any) => this.formatDate(value) }
  ];

  actions: TableAction[] = [
    {
      label: 'Editar',
      icon: 'fas fa-edit',
      action: 'edit',
      class: 'btn-warning'
    },
    {
      label: 'Excluir',
      icon: 'fas fa-trash',
      action: 'delete',
      class: 'btn-danger'
    }
  ];

  constructor(
    private router: Router,
    private profilesService: ProfilesService,
    private errorService: ErrorService,
    private authService: AuthService,
    private confirmationService: ConfirmationService
  ) {}

  ngOnInit(): void {
    this.loadProfiles();
  }

  /**
   * Carrega os perfis com paginação
   */
  loadProfiles(): void {
    this.loading = true;
    this.errorMessage = null;

    this.profilesService.getProfiles(this.currentPage, this.pageSize).subscribe({
      next: (response) => {
        this.profiles = response.data.map(dto => ({
          id: dto.Id,
          name: dto.Name,
          description: dto.Description,
          status: dto.Status,
          permissionOverrides: dto.PermissionOverrides || [],
          createdAt: new Date(dto.CreatedAt),
          updatedAt: new Date(dto.UpdatedAt),
          createdBy: dto.CreatedBy,
          updatedBy: dto.UpdatedBy,
          createdByName: dto.CreatedByName,
          updatedByName: dto.UpdatedByName || dto.UpdatedBy
        }));
        this.totalItems = response.pagination.totalCount;
        this.totalPages = response.pagination.totalPages;
        this.currentPage = response.pagination.currentPage;
        this.loading = false;
      },
      error: (error) => {
        console.error('❌ Erro ao carregar perfis:', error);
        this.errorMessage = 'Erro ao carregar perfis';
        this.errorService.showError('Erro ao carregar perfis');
        this.showError('Erro ao carregar perfis');
        this.loading = false;
      }
    });
  }

  /**
   * Busca perfis por termo
   */
  onSearch(searchTerm: string): void {
    this.searchTerm = searchTerm;
    this.currentPage = 1;
    
    if (searchTerm.trim() === '') {
      this.loadProfiles();
    } else {
      this.loading = true;
      this.profilesService.searchProfiles(searchTerm, this.currentPage, this.pageSize).subscribe({
        next: (response) => {
          this.profiles = response.data.map(dto => ({
            id: dto.Id,
            name: dto.Name,
            description: dto.Description,
            status: dto.Status,
            permissionOverrides: dto.PermissionOverrides || [],
            createdAt: new Date(dto.CreatedAt),
            updatedAt: new Date(dto.UpdatedAt),
            createdBy: dto.CreatedBy,
            updatedBy: dto.UpdatedBy,
            createdByName: dto.CreatedByName,
            updatedByName: dto.UpdatedByName || dto.UpdatedBy
          }));
          this.totalItems = response.pagination.totalCount;
          this.totalPages = response.pagination.totalPages;
          this.currentPage = response.pagination.currentPage;
          this.loading = false;
        },
        error: (error: any) => {
          console.error('❌ Erro ao buscar perfis:', error);
          this.errorService.showError('Erro ao buscar perfis');
          this.showError('Erro ao buscar perfis');
          this.loading = false;
        }
      });
    }
  }

  /**
   * Manipula mudança de tamanho de página (itens por página)
   */
  onPageSizeChange(size: number): void {
    if (!size || size <= 0) {
      return;
    }
    this.pageSize = size;
    this.currentPage = 1;
    this.loadProfiles();
  }

  /**
   * Manipula mudança de página
   */
  onPageChange(page: number): void {
    this.currentPage = page;
    this.loadProfiles();
  }

  /**
   * Manipula ações da tabela
   */
  handleAction(event: { action: string; item: Profile }): void {
    const { action, item } = event;

    switch (action) {
      case 'edit':
        this.editProfile(item);
        break;
      case 'delete':
        this.deleteProfile(item);
        break;
      default:
        break;
    }
  }

  /**
   * Edita um perfil
   */
  editProfile(profile: Profile): void {
    this.router.navigate(['/admin/profiles', profile.id, 'edit']);
  }

  /**
   * Exclui um perfil
   */
  deleteProfile(profile: Profile): void {
    this.confirmationService
      .confirmDelete(profile.name, 'o perfil')
      .pipe(take(1))
      .subscribe((confirmed) => {
        if (!confirmed) return;

        const currentUser = this.authService.currentUserValue;
        const userEmail = currentUser?.email || 'unknown@user.com';

        this.profilesService.deleteProfile(profile.id, userEmail).subscribe({
          next: () => {
            this.loadProfiles();
            this.showSuccess('Perfil excluído com sucesso!');
          },
          error: (error) => {
            console.error('Erro ao excluir perfil:', error);

            let message = 'Erro ao excluir perfil';
            if (error.status === 400 || error.status === 409) {
              message = 'Não é possível excluir este perfil pois ele está associado a usuários ou possui grupos vinculados.';
            } else if (error.error?.message) {
              message = error.error.message;
            }

            this.errorService.showError(message);
            this.showError(message);
          }
        });
      });
  }

  /**
   * Navega para a tela de criação de perfil
   */
  createProfile(): void {
    this.router.navigate(['/admin/profiles/new']);
  }

  private showSuccess(message: string): void {
    this.confirmationService
      .confirm({
        title: 'Sucesso!',
        message,
        type: 'info',
        confirmText: 'OK',
        confirmButtonClass: 'btn-primary',
        cancelButtonClass: 'd-none'
      })
      .pipe(take(1))
      .subscribe();
  }

  private showError(message: string): void {
    this.confirmationService
      .confirm({
        title: 'Erro',
        message,
        type: 'danger',
        confirmText: 'OK',
        confirmButtonClass: 'btn-danger',
        cancelButtonClass: 'd-none'
      })
      .pipe(take(1))
      .subscribe();
  }

  /**
   * Formata data para exibição
   */
  formatDate(date: Date | string | null): string {
    if (!date) return 'N/A';
    
    const d = new Date(date);
    if (isNaN(d.getTime())) return 'Data inválida';
    
    return d.toLocaleDateString('pt-BR', {
      day: '2-digit',
      month: '2-digit',
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  }
}
